import { logAppEvent } from "@/api/logs";
import { useConfirm } from "@/components/DialogProvider";
import { EditHoursDialog } from "@/components/EditHoursDialog";
import { LocationMapDialog } from "@/components/location-map-dialog";
import { DropdownMenuItem } from "@/components/shadcn-custom/ShadcnDropdownMenu";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useProjectContext } from "@/contexts/ProjectContext";
import { useProjectHours } from "@/features/projects/hooks/useProjectHours";
import { useUsers } from "@/features/users/hooks/useUsers";
import { useAllWorkers } from "@/features/workers/hooks/useWorkers";
import { db } from "@/firebase";
import {
  calculateLunchDuration,
  calculateWorkDuration,
  type ProjectHours,
  type WorkerModel,
} from "@/firestore_types";
import {
  getProjectWorkerAssignments,
  serializeProjectWorkerAssignments,
} from "@/lib/projectAssignmentHelpers";
import { genRandomId } from "@/lib/randomId";
import { type TimeEntryData, validateTimeEntry } from "@/lib/timeEntryValidation";
import {
  dateTimeLocalESTToDate,
  formatDateTimeShort,
  formatPhoneNumber,
  timestampToDateTimeLocalEST,
} from "@/lib/utils";
import { ProjectHourService } from "@/services/projectHourService";
import { createFileRoute } from "@tanstack/react-router";
import { doc, setDoc, Timestamp, updateDoc } from "firebase/firestore";
import {
  Calendar,
  CheckCheck,
  ChevronDown,
  ChevronUp,
  Clock,
  Edit,
  MapPin,
  NotebookIcon,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/dashboard/admin/projects/$projectId/hours/")({
  component: RouteComponent,
});

// Helper function to trigger native date/time picker
const triggerPicker = (elementId: string) => {
  const element = document.getElementById(elementId) as HTMLInputElement | null;
  if (element && "showPicker" in element) {
    (element as any).showPicker();
  }
};

function RouteComponent() {
  const { projectId } = Route.useParams();
  const { project } = useProjectContext();
  const { projectHours, loading } = useProjectHours(projectId);
  const { userProfile } = useAuth();
  const { workers: allWorkers } = useAllWorkers();
  const { users } = useUsers();
  const projectWorkerAssignments = useMemo(() => getProjectWorkerAssignments(project), [project]);
  const workerAssignmentsMap = useMemo(
    () => new Map(projectWorkerAssignments.map((assignment) => [assignment.workerId, assignment])),
    [projectWorkerAssignments],
  );
  const workers = allWorkers.filter((worker) => {
    const assignment = workerAssignmentsMap.get(worker.id);
    return Boolean(assignment);
  });

  // Separate workers into active and inactive
  const activeWorkers = useMemo(() => {
    return workers.filter((worker) => {
      const assignment = workerAssignmentsMap.get(worker.id);
      return !assignment?.inactive;
    });
  }, [workers, workerAssignmentsMap]);

  const confirm = useConfirm();

  // Get approver name by user ID
  const getApproverName = (userId?: string) => {
    if (!userId) return "N/A";
    const user = users.find((u) => u.uid === userId);
    return user?.name || user?.email || "Unknown User";
  };

  const [isMapDialogOpen, setIsMapDialogOpen] = useState(false);
  const [selectedLocationData, setSelectedLocationData] = useState<{
    clockInLocation?: { lat: number; lng: number };
    clockOutLocation?: { lat: number; lng: number };
    workerName: string;
    projectName: string;
  } | null>(null);

  const [expandedWorkers, setExpandedWorkers] = useState<string[]>([]);

  const toggleWorkerExpanded = (workerId: string) => {
    setExpandedWorkers(
      (prev) =>
        prev.includes(workerId)
          ? [] // Collapse if already expanded
          : [workerId], // Only expand this one, collapse all others
    );
  };

  // Manual hours dialog states
  const [isManualHoursModalOpen, setIsManualHoursModalOpen] = useState(false);
  const [selectedWorkerForHours, setSelectedWorkerForHours] = useState<WorkerModel | null>(null);
  const [addingManualHours, setAddingManualHours] = useState(false);
  const [manualHoursData, setManualHoursData] = useState<TimeEntryData>({
    clockInDateTime: "",
    clockOutDateTime: "",
    lunchStartDateTime: "",
    lunchEndDateTime: "",
    notes: "",
  });
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Edit hours dialog states
  const [isEditHoursModalOpen, setIsEditHoursModalOpen] = useState(false);
  const [editingHourEntry, setEditingHourEntry] = useState<ProjectHours | null>(null);
  const [editingWorker, setEditingWorker] = useState<WorkerModel | null>(null);
  const [updatingHours, setUpdatingHours] = useState(false);

  // Function to find project hours for a worker
  const getWorkerHours = (workerId: string) => {
    return projectHours.filter((ph) => ph.workerId === workerId);
  };

  // Function to open location map
  const openLocationMap = (hour: ProjectHours, workerName: string) => {
    setSelectedLocationData({
      clockInLocation: hour.clockInLocation,
      clockOutLocation: hour.clockedOutLocation,
      workerName: workerName,
      projectName: project?.name || "Unknown Project",
    });
    setIsMapDialogOpen(true);
  };

  // Manual hours functions
  const resetManualHoursForm = () => {
    setManualHoursData({
      clockInDateTime: "",
      clockOutDateTime: "",
      lunchStartDateTime: "",
      lunchEndDateTime: "",
      notes: "",
    });
    setValidationErrors([]);
  };

  const handleAddManualHours = (worker: WorkerModel) => {
    setSelectedWorkerForHours(worker);
    // Set default clock in time to now in EST
    const now = Timestamp.now();
    const nowString = timestampToDateTimeLocalEST(now);
    setManualHoursData({
      ...manualHoursData,
      clockInDateTime: nowString,
    });
    setIsManualHoursModalOpen(true);
  };

  const handleEditHours = (hour: ProjectHours, worker: WorkerModel) => {
    setEditingHourEntry(hour);
    setEditingWorker(worker);
    setIsEditHoursModalOpen(true);
  };

  const submitManualHours = async () => {
    // Validate the form
    const errors = validateTimeEntry(manualHoursData);
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    if (!selectedWorkerForHours || !project || !manualHoursData.clockInDateTime) return;

    setAddingManualHours(true);
    try {
      const currentUserId = userProfile?.uid;
      if (!currentUserId) {
        toast.info("Invalid user session");
        return;
      }

      // Create date-time objects from datetime-local inputs (interpreting as EST)
      const clockInDateTime = dateTimeLocalESTToDate(manualHoursData.clockInDateTime);
      const clockOutDateTime = manualHoursData.clockOutDateTime
        ? dateTimeLocalESTToDate(manualHoursData.clockOutDateTime)
        : null;
      const lunchStartDateTime = manualHoursData.lunchStartDateTime
        ? dateTimeLocalESTToDate(manualHoursData.lunchStartDateTime)
        : undefined;
      const lunchEndDateTime = manualHoursData.lunchEndDateTime
        ? dateTimeLocalESTToDate(manualHoursData.lunchEndDateTime)
        : undefined;

      // Generate the clock ID
      const workDateString = clockInDateTime.toISOString().split("T")[0];
      const clockId = `${projectId}-${selectedWorkerForHours.id}-${genRandomId()}`;

      // Get assigned workers data for the project
      const assignedWorkers = serializeProjectWorkerAssignments(projectWorkerAssignments);

      // Create ProjectHours entry
      const projectHoursRef = doc(db, "ProjectHours", clockId);

      const projectHoursData = {
        customerId: project.customerId,
        assignedManagerIds: project.assignedManagerIds || [],
        projectId: projectId,
        workerId: selectedWorkerForHours.id,
        isDeleted: false,
        clockInLocation: {
          lat: 0, // Default location for manual entries
          lng: 0,
        },
        clockedInAt: Timestamp.fromDate(clockInDateTime),
        clockedIn: true,
        clockedOut: clockOutDateTime ? true : false,
        assignedWorkers: assignedWorkers,
        approved: clockOutDateTime ? true : false, // Only approve completed entries
        manualEntry: true, // Flag to indicate this was manually added
        manualEntryBy: currentUserId,
        manualEntryNotes: manualHoursData.notes || "Manual entry by admin",
        ...(clockOutDateTime && {
          clockedOutAt: Timestamp.fromDate(clockOutDateTime),
          approvedAt: Timestamp.now(),
          approvedStatusChangedBy: currentUserId,
          approvedStatusChangedAt: Timestamp.now(),
        }),
        ...(lunchStartDateTime &&
          lunchEndDateTime && {
            lunchStartTime: Timestamp.fromDate(lunchStartDateTime),
            lunchEndTime: Timestamp.fromDate(lunchEndDateTime),
            onLunch: false, // Lunch is completed for manual entries
          }),
      };

      await setDoc(projectHoursRef, projectHoursData);

      // Log the manual entry event
      logAppEvent("manual_project_hours_added", {
        workerId: selectedWorkerForHours.id,
        projectId: projectId,
        workDate: workDateString,
        managerId: currentUserId,
      });

      setIsManualHoursModalOpen(false);
      setSelectedWorkerForHours(null);
      resetManualHoursForm();
      toast.info("Manual hours added successfully!");
    } catch (error) {
      console.error("Error adding manual hours:", error);
      toast.info("Failed to add manual hours. Please try again.");
    } finally {
      setAddingManualHours(false);
    }
  };

  const submitEditHours = async (formData: TimeEntryData) => {
    if (!editingHourEntry || !editingWorker || !project) return;

    setUpdatingHours(true);
    try {
      const currentUserId = userProfile?.uid;
      if (!currentUserId) {
        toast.info("Invalid user session");
        return;
      }

      // Create date-time objects from datetime-local inputs (interpreting as EST)
      const clockInDateTime = dateTimeLocalESTToDate(formData.clockInDateTime);
      const clockOutDateTime = formData.clockOutDateTime
        ? dateTimeLocalESTToDate(formData.clockOutDateTime)
        : null;
      const lunchStartDateTime = formData.lunchStartDateTime
        ? dateTimeLocalESTToDate(formData.lunchStartDateTime)
        : undefined;
      const lunchEndDateTime = formData.lunchEndDateTime
        ? dateTimeLocalESTToDate(formData.lunchEndDateTime)
        : undefined;

      // Update ProjectHours entry
      const projectHoursRef = doc(db, "ProjectHours", editingHourEntry.id);
      const updateData = {
        clockedInAt: Timestamp.fromDate(clockInDateTime),
        clockedIn: true,
        clockedOut: clockOutDateTime ? true : false,
        approved: clockOutDateTime ? true : false,
        manualEntryNotes: formData.notes || "Updated by admin",
        lastEditedAt: Timestamp.now(),
        lastEditedBy: currentUserId,
        ...(clockOutDateTime
          ? {
              clockedOutAt: Timestamp.fromDate(clockOutDateTime),
              approvedAt: Timestamp.now(),
              approvedStatusChangedBy: currentUserId,
              approvedStatusChangedAt: Timestamp.now(),
            }
          : {
              clockedOutAt: null,
              approvedAt: null,
              approvedStatusChangedBy: null,
              approvedStatusChangedAt: null,
            }),
        ...(lunchStartDateTime && lunchEndDateTime
          ? {
              lunchStartTime: Timestamp.fromDate(lunchStartDateTime),
              lunchEndTime: Timestamp.fromDate(lunchEndDateTime),
              onLunch: false,
            }
          : {
              lunchStartTime: null,
              lunchEndTime: null,
              onLunch: false,
            }),
      };

      await updateDoc(projectHoursRef, updateData);

      // Log the edit event
      logAppEvent("manual_project_hours_added", {
        workerId: editingWorker.id,
        projectId: projectId,
        workDate: clockInDateTime.toISOString().split("T")[0],
        managerId: currentUserId,
      });

      setIsEditHoursModalOpen(false);
      setEditingHourEntry(null);
      setEditingWorker(null);
      toast.info("Hours updated successfully!");
    } catch (error) {
      console.error("Error updating hours:", error);
      toast.info("Failed to update hours. Please try again.");
    } finally {
      setUpdatingHours(false);
    }
  };

  const handleDeleteHour = async (hourId: string) => {
    const confirmation = await confirm({
      title: "Move time entry to trash?",
      description: "The time entry will be hidden and can be restored from the trash section.",
      confirmText: "Move to Trash",
      cancelText: "Cancel",
    });

    if (confirmation) {
      try {
        await updateDoc(doc(db, "ProjectHours", hourId), { isDeleted: true });
        toast.info("Time entry moved to trash.");
      } catch (error) {
        console.error("Error deleting time entry:", error);
        toast.info("Failed to delete time entry. Please try again.");
      }
    }
  };

  const handleApproveAll = async (workerId: string) => {
    const workerHours = getWorkerHours(workerId);
    // Filter for entries that are completed (clocked out)
    const completedEntries = workerHours.filter((hour) => hour.clockedOut);

    if (completedEntries.length === 0) {
      toast.info("No completed entries to modify");
      return;
    }

    // Check if all completed entries are already approved
    const allApproved = completedEntries.every((hour) => hour.approved);

    // If not all are approved, we want to approve the unapproved ones
    // If all are approved, we want to unapprove all of them
    const targetEntries = allApproved
      ? completedEntries
      : completedEntries.filter((hour) => !hour.approved);

    const actionText = allApproved ? "Unapprove" : "Approve";
    const titleText = allApproved ? "Unapprove all time entries?" : "Approve all time entries?";
    const descriptionText = allApproved
      ? `This will unapprove ${targetEntries.length} time ${targetEntries.length === 1 ? "entry" : "entries"}.`
      : `This will approve ${targetEntries.length} time ${targetEntries.length === 1 ? "entry" : "entries"}.`;

    const confirmation = await confirm({
      title: titleText,
      description: descriptionText,
      confirmText: `${actionText} All`,
      cancelText: "Cancel",
    });

    if (confirmation) {
      try {
        await Promise.all(
          targetEntries.map((hour) =>
            ProjectHourService.changeApprovedStatus(hour.id, !allApproved),
          ),
        );
        toast.info(
          `${actionText}d ${targetEntries.length} time ${targetEntries.length === 1 ? "entry" : "entries"}`,
        );
      } catch (error) {
        console.error(`Error ${actionText.toLowerCase()}ing entries:`, error);
        toast.info(`Failed to ${actionText.toLowerCase()} all entries. Please try again.`);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-32 w-32 animate-spin rounded-full border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Tab Content */}
      <div className="mt-4">
        {activeWorkers.length === 0 ? (
          <EmptyWorkersView />
        ) : (
          <div className="flex flex-col gap-4">
            {activeWorkers.map((worker) => {
              const workerHours = getWorkerHours(worker.id);
              const approvalRequiredEntries = workerHours.filter(
                (hour) => !hour.approved && hour.clockedOut,
              );
              const assignment = workerAssignmentsMap.get(worker.id);

              // Calculate worker initials
              const initials = worker.name
                .split(" ")
                .map((n) => n.charAt(0).toUpperCase())
                .join("")
                .slice(0, 2);

              // Determine status
              const hasActiveEntry = workerHours.some((hour) => hour.clockedIn && !hour.clockedOut);
              const status = hasActiveEntry
                ? "Work in progress"
                : approvalRequiredEntries.length > 0
                  ? "Approval required"
                  : "Approved";

              const completedEntries = workerHours.filter((hour) => hour.clockedOut);
              const areAllCompletedEntriesApproved =
                completedEntries.length > 0 && completedEntries.every((hour) => hour.approved);

              return (
                <div key={worker.id} className="rounded-lg border border-gray-200 bg-white">
                  <button
                    onClick={() => toggleWorkerExpanded(worker.id)}
                    className="flex w-full flex-col gap-3 p-4 text-left transition-colors hover:bg-gray-50"
                  >
                    <div className="flex items-start justify-between gap-3 min-[744px]:items-center">
                      <div className="flex min-w-0 flex-1 items-start gap-3 min-[744px]:items-center">
                        {expandedWorkers.includes(worker.id) ? (
                          <ChevronUp className="h-5 w-5 flex-shrink-0 text-gray-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 flex-shrink-0 text-gray-400" />
                        )}
                        <div className="bg-[#F3F4F6]-100 flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 min-[744px]:h-12 min-[744px]:w-12">
                          <span className="text-sm font-semibold text-black min-[744px]:text-base">
                            {initials}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="mb-1 truncate text-base font-semibold text-gray-900">
                            {worker.name}
                          </p>
                          <p className="truncate text-sm text-gray-600">
                            {formatPhoneNumber(worker.phoneNumber)}
                          </p>
                        </div>

                        <div className="hidden flex-wrap items-center gap-4 min-[744px]:flex">
                          {assignment?.workType && (
                            <span className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium whitespace-nowrap text-gray-700">
                              {assignment.workType}
                            </span>
                          )}
                          <span className="text-[#62748E]-900 text-sm font-medium whitespace-nowrap">
                            {workerHours.length} {workerHours.length === 1 ? "entry" : "entries"}
                          </span>
                          <span
                            className={`flex items-center gap-1 px-2 py-0.5 text-xs font-medium whitespace-nowrap ${
                              hasActiveEntry
                                ? "text-blue-700"
                                : approvalRequiredEntries.length > 0
                                  ? "text-[#FF6900]-700"
                                  : "text-green-700"
                            }`}
                          >
                            <span
                              className={`h-1.5 w-1.5 rounded-full ${
                                hasActiveEntry
                                  ? "bg-blue-700"
                                  : approvalRequiredEntries.length > 0
                                    ? "bg-[#FF6900]-700"
                                    : "bg-green-700"
                              }`}
                            ></span>
                            {status}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddManualHours(worker);
                          }}
                          className="hidden items-center gap-2 rounded-md border border-purple-600 px-4 py-2 text-sm font-medium text-purple-600 transition-colors hover:bg-purple-50 min-[744px]:inline-flex"
                        >
                          <Plus className="h-4 w-4" />
                          Add Hours
                        </button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              className="rounded p-1.5 hover:bg-gray-100"
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M8 8.66667C8.36819 8.66667 8.66667 8.36819 8.66667 8C8.66667 7.63181 8.36819 7.33333 8 7.33333C7.63181 7.33333 7.33333 7.63181 7.33333 8C7.33333 8.36819 7.63181 8.66667 8 8.66667Z"
                                  stroke="#475467"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M8 4.00001C8.36819 4.00001 8.66667 3.70153 8.66667 3.33334C8.66667 2.96515 8.36819 2.66667 8 2.66667C7.63181 2.66667 7.33333 2.96515 7.33333 3.33334C7.33333 3.70153 7.63181 4.00001 8 4.00001Z"
                                  stroke="#475467"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M8 13.3333C8.36819 13.3333 8.66667 13.0349 8.66667 12.6667C8.66667 12.2985 8.36819 12 8 12C7.63181 12 7.33333 12.2985 7.33333 12.6667C7.33333 13.0349 7.63181 13.3333 8 13.3333Z"
                                  stroke="#475467"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                handleApproveAll(worker.id);
                              }}
                              icon={
                                areAllCompletedEntriesApproved ? (
                                  <X className="h-4 w-4" />
                                ) : (
                                  <CheckCheck className="h-4 w-4" />
                                )
                              }
                            >
                              <span className="whitespace-nowrap">
                                {areAllCompletedEntriesApproved ? "Unapprove all" : "Approve all"}
                              </span>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 min-[744px]:hidden">
                      {assignment?.workType && (
                        <span className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium whitespace-nowrap text-gray-700">
                          {assignment.workType}
                        </span>
                      )}
                      <span className="text-sm font-medium whitespace-nowrap text-gray-900">
                        {workerHours.length} {workerHours.length === 1 ? "entry" : "entries"}
                      </span>
                      <span
                        className={`flex items-center gap-1 px-2 py-0.5 text-xs font-medium whitespace-nowrap ${
                          hasActiveEntry
                            ? "text-blue-700"
                            : approvalRequiredEntries.length > 0
                              ? "text-orange-700"
                              : "text-green-700"
                        }`}
                      >
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${
                            hasActiveEntry
                              ? "bg-blue-700"
                              : approvalRequiredEntries.length > 0
                                ? "bg-orange-700"
                                : "bg-green-700"
                          }`}
                        ></span>
                        {status}
                      </span>
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddManualHours(worker);
                      }}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-purple-600 px-4 py-2 text-sm font-medium text-purple-600 transition-colors hover:bg-purple-50 min-[744px]:hidden"
                    >
                      <Plus className="h-4 w-4" />
                      Add Hours
                    </button>
                  </button>

                  {/* Expanded Content - Time Entries Table */}
                  {expandedWorkers.includes(worker.id) && (
                    <div className="border-t border-gray-200 bg-gray-50 p-4">
                      <div className="overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        <table className="w-full rounded-lg border border-[#D6DBE3] text-sm">
                          <thead>
                            <tr className="border-b border-[#D6DBE3]">
                              <th className="px-4 py-3 text-left font-medium whitespace-nowrap text-gray-700">
                                Clock In ↕
                              </th>
                              <th className="px-4 py-3 text-left font-medium whitespace-nowrap text-gray-700">
                                Clock Out ↕
                              </th>
                              <th className="px-4 py-3 text-left font-medium whitespace-nowrap text-gray-700">
                                Duration ↕
                              </th>
                              <th className="px-4 py-3 text-left font-medium whitespace-nowrap text-gray-700">
                                Lunch Break
                              </th>
                              <th className="px-4 py-3 text-left font-medium whitespace-nowrap text-gray-700">
                                Status ↕
                              </th>
                              <th className="px-4 py-3 text-left font-medium whitespace-nowrap text-gray-700">
                                Approved By
                              </th>
                              <th className="px-4 py-3 text-left font-medium whitespace-nowrap text-gray-700">
                                Agency Note
                              </th>
                              <th className="px-4 py-3 text-right font-medium text-gray-700"></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-[#D6DBE3] bg-white">
                            {workerHours
                              .sort((a, b) => b.clockedInAt.toMillis() - a.clockedInAt.toMillis())
                              .map((hour) => (
                                <tr key={hour.id}>
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <div className="flex items-center gap-1.5 text-gray-900">
                                      {hour.clockInLocation && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => openLocationMap(hour, worker.name)}
                                          className="h-auto p-0 text-black"
                                        >
                                          <MapPin className="h-3 w-3" />
                                        </Button>
                                      )}
                                      <div className="text-sm">
                                        <div className="text-gray-600">
                                          {formatDateTimeShort(hour.clockedInAt)}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-gray-500">
                                    {hour.clockedOutAt ? (
                                      <div className="flex items-center gap-1.5 text-gray-900">
                                        {hour.clockedOutLocation && (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => openLocationMap(hour, worker.name)}
                                            className="h-auto p-0 text-black"
                                          >
                                            <MapPin className="h-3 w-3" />
                                          </Button>
                                        )}
                                        <div className="text-sm">
                                          <div className="text-gray-600">
                                            {formatDateTimeShort(hour.clockedOutAt)}
                                          </div>
                                        </div>
                                      </div>
                                    ) : (
                                      "Still clocked in"
                                    )}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-gray-900">
                                    {hour.clockedOutAt ? (
                                      <span className="font-medium text-black">
                                        {calculateWorkDuration(hour)?.formattedDecimal}
                                      </span>
                                    ) : (
                                      <span className="animate-pulse font-medium text-black">
                                        -
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-gray-900">
                                    {hour.lunchStartTime ? (
                                      <div className="space-y-1">
                                        <div className="text-xs font-medium">
                                          {calculateLunchDuration(hour)?.formatted ||
                                            (hour.onLunch ? (
                                              <span className="text-orange-600">In Progress</span>
                                            ) : (
                                              <span className="text-green-600">Taken</span>
                                            ))}
                                        </div>
                                      </div>
                                    ) : (
                                      "-"
                                    )}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <span
                                      className={`inline-flex items-center gap-1 text-sm ${
                                        hour.approved
                                          ? "text-green-600"
                                          : hour.clockedOut
                                            ? "text-orange-600"
                                            : "text-blue-600"
                                      }`}
                                    >
                                      <span
                                        className={`h-1.5 w-1.5 rounded-full ${
                                          hour.approved
                                            ? "bg-green-600"
                                            : hour.clockedOut
                                              ? "bg-orange-600"
                                              : "bg-blue-600"
                                        }`}
                                      ></span>
                                      {hour.approved
                                        ? "Approved"
                                        : hour.clockedOut
                                          ? "Approval required"
                                          : "Work in progress"}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    {hour.approvedStatusChangedBy ? (
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-gray-900">
                                          {hour.approved ? "Approved" : "Unapproved"} by{" "}
                                          {getApproverName(hour.approvedStatusChangedBy)}
                                        </span>
                                        {hour.approvedStatusChangedAt && (
                                          <span className="text-xs text-gray-500">
                                            • {formatDateTimeShort(hour.approvedStatusChangedAt)}
                                          </span>
                                        )}
                                      </div>
                                    ) : (
                                      "-"
                                    )}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-gray-900">
                                    {hour.agencyNote || "-"}
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex items-center justify-end gap-2">
                                      {hour.clockedIn && hour.clockedOut && (
                                        <button
                                          onClick={async () => {
                                            const isApproving = !hour.approved;
                                            const confirmation = await confirm({
                                              title: isApproving
                                                ? "Approve time entry?"
                                                : "Unapprove time entry?",
                                              description: isApproving
                                                ? "This will approve the time entry."
                                                : "This will unapprove the time entry.",
                                              confirmText: isApproving ? "Approve" : "Unapprove",
                                              cancelText: "Cancel",
                                            });
                                            if (confirmation) {
                                              try {
                                                await ProjectHourService.changeApprovedStatus(
                                                  hour.id,
                                                  isApproving,
                                                );
                                              } catch (error) {
                                                console.error(
                                                  "Error changing approval status:",
                                                  error,
                                                );
                                                toast.error(
                                                  "Failed to change approval status. Please try again.",
                                                );
                                              }
                                            }
                                          }}
                                          className="rounded border border-purple-600 px-3 py-1 text-sm font-medium text-purple-600 hover:bg-purple-50"
                                        >
                                          {hour.approved ? "Unapprove" : "Approve"}
                                        </button>
                                      )}
                                      <button
                                        onClick={() => handleEditHours(hour, worker)}
                                        className="rounded p-1 hover:bg-gray-100"
                                      >
                                        <Edit className="h-4 w-4 text-blue-600" />
                                      </button>
                                      {hour.manualEntry && hour.manualEntryNotes && (
                                        <button
                                          onClick={() => {
                                            toast.info(hour.manualEntryNotes);
                                          }}
                                          className="rounded p-1 hover:bg-gray-100"
                                        >
                                          <NotebookIcon className="h-4 w-4 text-gray-600" />
                                        </button>
                                      )}
                                      <button
                                        onClick={() => handleDeleteHour(hour.id)}
                                        className="rounded p-1 hover:bg-gray-100"
                                      >
                                        <Trash2 className="h-4 w-4 text-red-600" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Manual Hours Dialog */}
      <Dialog open={isManualHoursModalOpen} onOpenChange={setIsManualHoursModalOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[500px]">
          <DialogHeader className="space-y-1">
            <DialogTitle className="text-xl font-semibold text-gray-900">
              Add Hours for {selectedWorkerForHours?.name}
            </DialogTitle>
            <p className="text-sm text-gray-500">
              This entry will be automatically approved and marked as manually add
            </p>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Validation Errors */}
            {validationErrors.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <div className="flex items-start">
                  <svg
                    className="mt-0.5 mr-2 h-5 w-5 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-red-800">
                      Please fix the following errors:
                    </p>
                    <ul className="mt-1 list-inside list-disc text-sm text-red-600">
                      {validationErrors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Clock In Section */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label
                  htmlFor="clock-in-date"
                  className="mb-2 block text-sm font-medium text-gray-900"
                >
                  Clock In date
                </Label>
                <div className="relative">
                  <Input
                    id="clock-in-date"
                    type="date"
                    value={manualHoursData.clockInDateTime?.split("T")[0] || ""}
                    onChange={(e) => {
                      const time = manualHoursData.clockInDateTime?.split("T")[1] || "12:00";
                      setManualHoursData({
                        ...manualHoursData,
                        clockInDateTime: e.target.value ? `${e.target.value}T${time}` : "",
                      });
                      setValidationErrors([]);
                    }}
                    className={`h-10 w-full appearance-none bg-white pr-10 [&::-webkit-calendar-picker-indicator]:pointer-events-none [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-inner-spin-button]:hidden ${!manualHoursData.clockInDateTime?.split("T")[0] ? "[&::-webkit-datetime-edit]:opacity-0 [&::-webkit-datetime-edit-fields-wrapper]:opacity-0" : ""}`}
                  />
                  {!manualHoursData.clockInDateTime?.split("T")[0] && (
                    <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-gray-500">
                      Select clock In date
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => triggerPicker("clock-in-date")}
                    className={`absolute top-1/2 right-3 -translate-y-1/2 ${manualHoursData.clockInDateTime?.split("T")[0] ? "text-gray-900" : "text-gray-400 hover:text-gray-600"}`}
                  >
                    <Calendar className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div>
                <Label
                  htmlFor="clock-in-time"
                  className="mb-2 block text-sm font-medium text-gray-900"
                >
                  Time
                </Label>
                <div className="relative">
                  <Input
                    id="clock-in-time"
                    type="time"
                    value={manualHoursData.clockInDateTime?.split("T")[1]?.substring(0, 5) || ""}
                    onChange={(e) => {
                      const date =
                        manualHoursData.clockInDateTime?.split("T")[0] ||
                        new Date().toISOString().split("T")[0];
                      setManualHoursData({
                        ...manualHoursData,
                        clockInDateTime: `${date}T${e.target.value}`,
                      });
                      setValidationErrors([]);
                    }}
                    className={`h-10 w-full appearance-none bg-white pr-10 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-inner-spin-button]:hidden ${!manualHoursData.clockInDateTime?.split("T")[1] ? "[&::-webkit-datetime-edit]:opacity-0 [&::-webkit-datetime-edit-fields-wrapper]:opacity-0" : ""}`}
                  />
                  {!manualHoursData.clockInDateTime?.split("T")[1] && (
                    <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-gray-500">
                      Select time
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => triggerPicker("clock-in-time")}
                    className={`absolute top-1/2 right-3 -translate-y-1/2 ${manualHoursData.clockInDateTime?.split("T")[1] ? "text-gray-900" : "text-gray-400 hover:text-gray-600"}`}
                  >
                    <Clock className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Clock Out Section */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label
                  htmlFor="clock-out-date"
                  className="mb-2 block text-sm font-medium text-gray-900"
                >
                  Clock Out date
                </Label>
                <div className="relative">
                  <Input
                    id="clock-out-date"
                    type="date"
                    value={manualHoursData.clockOutDateTime?.split("T")[0] || ""}
                    onChange={(e) => {
                      const time = manualHoursData.clockOutDateTime?.split("T")[1] || "12:00";
                      setManualHoursData({
                        ...manualHoursData,
                        clockOutDateTime: e.target.value ? `${e.target.value}T${time}` : "",
                      });
                      setValidationErrors([]);
                    }}
                    className={`h-10 w-full appearance-none bg-white pr-10 [&::-webkit-calendar-picker-indicator]:pointer-events-none [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-inner-spin-button]:hidden ${!manualHoursData.clockOutDateTime?.split("T")[0] ? "[&::-webkit-datetime-edit]:opacity-0 [&::-webkit-datetime-edit-fields-wrapper]:opacity-0" : ""}`}
                  />
                  {!manualHoursData.clockOutDateTime?.split("T")[0] && (
                    <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-gray-500">
                      Select clock Out date
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => triggerPicker("clock-out-date")}
                    className={`absolute top-1/2 right-3 -translate-y-1/2 ${manualHoursData.clockOutDateTime?.split("T")[0] ? "text-gray-900" : "text-gray-400 hover:text-gray-600"}`}
                  >
                    <Calendar className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div>
                <Label
                  htmlFor="clock-out-time"
                  className="mb-2 block text-sm font-medium text-gray-900"
                >
                  Time
                </Label>
                <div className="relative">
                  <Input
                    id="clock-out-time"
                    type="time"
                    value={manualHoursData.clockOutDateTime?.split("T")[1]?.substring(0, 5) || ""}
                    onChange={(e) => {
                      const date =
                        manualHoursData.clockOutDateTime?.split("T")[0] ||
                        new Date().toISOString().split("T")[0];
                      setManualHoursData({
                        ...manualHoursData,
                        clockOutDateTime: `${date}T${e.target.value}`,
                      });
                      setValidationErrors([]);
                    }}
                    className={`h-10 w-full appearance-none bg-white pr-10 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-inner-spin-button]:hidden ${!manualHoursData.clockOutDateTime?.split("T")[1] ? "[&::-webkit-datetime-edit]:opacity-0 [&::-webkit-datetime-edit-fields-wrapper]:opacity-0" : ""}`}
                  />
                  {!manualHoursData.clockOutDateTime?.split("T")[1] && (
                    <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-gray-500">
                      Select time
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => triggerPicker("clock-out-time")}
                    className={`absolute top-1/2 right-3 -translate-y-1/2 ${manualHoursData.clockOutDateTime?.split("T")[1] ? "text-gray-900" : "text-gray-400 hover:text-gray-600"}`}
                  >
                    <Clock className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Lunch Break Toggle */}
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium text-gray-900">Lunch break</Label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  role="switch"
                  aria-checked={Boolean(manualHoursData.lunchStartDateTime)}
                  onClick={() => {
                    if (manualHoursData.lunchStartDateTime) {
                      // Turn off - clear lunch times
                      setManualHoursData({
                        ...manualHoursData,
                        lunchStartDateTime: "",
                        lunchEndDateTime: "",
                      });
                    } else {
                      // Turn on - set default lunch time
                      const clockInDate =
                        manualHoursData.clockInDateTime?.split("T")[0] ||
                        new Date().toISOString().split("T")[0];
                      setManualHoursData({
                        ...manualHoursData,
                        lunchStartDateTime: `${clockInDate}T12:00`,
                        lunchEndDateTime: `${clockInDate}T12:30`,
                      });
                    }
                  }}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 focus-visible:outline-none ${
                    manualHoursData.lunchStartDateTime ? "bg-purple-600" : "bg-gray-200"
                  }`}
                >
                  <span
                    className={`pointer-events-none block h-5 w-5 rounded-full bg-white shadow-lg ring-0 transition-transform ${
                      manualHoursData.lunchStartDateTime ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </button>
                <span className="text-sm text-gray-600">
                  {manualHoursData.lunchStartDateTime ? "Taken" : "Not taken"}
                </span>
              </div>
            </div>

            {/* Lunch Break Date/Time (shown when toggle is on) */}
            {manualHoursData.lunchStartDateTime && (
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label
                    htmlFor="lunch-break-date"
                    className="mb-2 block text-sm font-medium text-gray-900"
                  >
                    Lunch break date
                  </Label>
                  <div className="relative">
                    <Input
                      id="lunch-break-date"
                      type="date"
                      value={manualHoursData.lunchStartDateTime?.split("T")[0] || ""}
                      onChange={(e) => {
                        const startTime =
                          manualHoursData.lunchStartDateTime?.split("T")[1] || "12:00";
                        const endTime = manualHoursData.lunchEndDateTime?.split("T")[1] || "12:30";
                        setManualHoursData({
                          ...manualHoursData,
                          lunchStartDateTime: e.target.value
                            ? `${e.target.value}T${startTime}`
                            : "",
                          lunchEndDateTime: e.target.value ? `${e.target.value}T${endTime}` : "",
                        });
                        setValidationErrors([]);
                      }}
                      className={`h-10 w-full appearance-none bg-white pr-10 [&::-webkit-calendar-picker-indicator]:pointer-events-none [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-inner-spin-button]:hidden ${!manualHoursData.lunchStartDateTime?.split("T")[0] ? "[&::-webkit-datetime-edit]:opacity-0 [&::-webkit-datetime-edit-fields-wrapper]:opacity-0" : ""}`}
                    />
                    {!manualHoursData.lunchStartDateTime?.split("T")[0] && (
                      <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-gray-500">
                        Select date
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => triggerPicker("lunch-break-date")}
                      className={`absolute top-1/2 right-3 -translate-y-1/2 ${manualHoursData.lunchStartDateTime?.split("T")[0] ? "text-gray-900" : "text-gray-400 hover:text-gray-600"}`}
                    >
                      <Calendar className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div>
                  <Label
                    htmlFor="lunch-start-time"
                    className="mb-2 block text-sm font-medium text-gray-900"
                  >
                    Start time
                  </Label>
                  <div className="relative">
                    <Input
                      id="lunch-start-time"
                      type="time"
                      value={
                        manualHoursData.lunchStartDateTime?.split("T")[1]?.substring(0, 5) || ""
                      }
                      onChange={(e) => {
                        const date =
                          manualHoursData.lunchStartDateTime?.split("T")[0] ||
                          new Date().toISOString().split("T")[0];
                        setManualHoursData({
                          ...manualHoursData,
                          lunchStartDateTime: `${date}T${e.target.value}`,
                        });
                        setValidationErrors([]);
                      }}
                      className={`h-10 w-full appearance-none bg-white pr-10 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-inner-spin-button]:hidden ${!manualHoursData.lunchStartDateTime?.split("T")[1] ? "[&::-webkit-datetime-edit]:opacity-0 [&::-webkit-datetime-edit-fields-wrapper]:opacity-0" : ""}`}
                    />
                    {!manualHoursData.lunchStartDateTime?.split("T")[1] && (
                      <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-gray-500">
                        Select time
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => triggerPicker("lunch-start-time")}
                      className={`absolute top-1/2 right-3 -translate-y-1/2 ${manualHoursData.lunchStartDateTime?.split("T")[1] ? "text-gray-900" : "text-gray-400 hover:text-gray-600"}`}
                    >
                      <Clock className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div>
                  <Label
                    htmlFor="lunch-end-time"
                    className="mb-2 block text-sm font-medium text-gray-900"
                  >
                    End time
                  </Label>
                  <div className="relative">
                    <Input
                      id="lunch-end-time"
                      type="time"
                      value={manualHoursData.lunchEndDateTime?.split("T")[1]?.substring(0, 5) || ""}
                      onChange={(e) => {
                        const date =
                          manualHoursData.lunchEndDateTime?.split("T")[0] ||
                          new Date().toISOString().split("T")[0];
                        setManualHoursData({
                          ...manualHoursData,
                          lunchEndDateTime: `${date}T${e.target.value}`,
                        });
                        setValidationErrors([]);
                      }}
                      className={`h-10 w-full appearance-none bg-white pr-10 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-inner-spin-button]:hidden ${!manualHoursData.lunchEndDateTime?.split("T")[1] ? "[&::-webkit-datetime-edit]:opacity-0 [&::-webkit-datetime-edit-fields-wrapper]:opacity-0" : ""}`}
                    />
                    {!manualHoursData.lunchEndDateTime?.split("T")[1] && (
                      <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-gray-500">
                        Select time
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => triggerPicker("lunch-end-time")}
                      className={`absolute top-1/2 right-3 -translate-y-1/2 ${manualHoursData.lunchEndDateTime?.split("T")[1] ? "text-gray-900" : "text-gray-400 hover:text-gray-600"}`}
                    >
                      <Clock className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <Label htmlFor="notes" className="mb-2 block text-sm font-medium text-gray-900">
                Notes
              </Label>
              <textarea
                id="notes"
                value={manualHoursData.notes}
                onChange={(e) =>
                  setManualHoursData({
                    ...manualHoursData,
                    notes: e.target.value,
                  })
                }
                className="h-24 w-full resize-none rounded-md border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:outline-none"
                placeholder="Enter additional notes"
              />
            </div>
          </div>

          <DialogFooter className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => {
                setIsManualHoursModalOpen(false);
                setSelectedWorkerForHours(null);
                resetManualHoursForm();
              }}
              className="rounded-md border border-gray-300 bg-white px-6 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              disabled={addingManualHours}
            >
              Cancel
            </button>
            <button
              onClick={submitManualHours}
              disabled={addingManualHours}
              className="rounded-md bg-purple-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50"
            >
              {addingManualHours ? "Saving..." : "Save and Add"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Hours Dialog */}
      {editingHourEntry && editingWorker && (
        <EditHoursDialog
          isOpen={isEditHoursModalOpen}
          onClose={() => {
            setIsEditHoursModalOpen(false);
            setEditingHourEntry(null);
            setEditingWorker(null);
          }}
          onSubmit={submitEditHours}
          workerName={editingWorker.name}
          projectName={project?.name || "Unknown Project"}
          initialData={{
            clockInDateTime: timestampToDateTimeLocalEST(editingHourEntry.clockedInAt),
            clockOutDateTime: editingHourEntry.clockedOutAt
              ? timestampToDateTimeLocalEST(editingHourEntry.clockedOutAt)
              : "",
            lunchStartDateTime: editingHourEntry.lunchStartTime
              ? timestampToDateTimeLocalEST(editingHourEntry.lunchStartTime)
              : "",
            lunchEndDateTime: editingHourEntry.lunchEndTime
              ? timestampToDateTimeLocalEST(editingHourEntry.lunchEndTime)
              : "",
            notes: editingHourEntry.manualEntryNotes || "",
          }}
          isSubmitting={updatingHours}
        />
      )}

      {/* Location Map Dialog */}
      {selectedLocationData && (
        <LocationMapDialog
          isOpen={isMapDialogOpen}
          onClose={() => setIsMapDialogOpen(false)}
          clockInLocation={selectedLocationData.clockInLocation}
          clockOutLocation={selectedLocationData.clockOutLocation}
          workerName={selectedLocationData.workerName}
          projectName={selectedLocationData.projectName}
        />
      )}
    </div>
  );
}

export function EmptyWorkersView() {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <svg
        width="160"
        height="80"
        viewBox="0 0 160 80"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="mb-6"
      >
        <path
          d="M64.5723 13.7856C61.7319 13.7856 59.4294 16.0882 59.4294 18.9285C59.4294 21.7688 61.7319 24.0714 64.5723 24.0714H34.858C32.0177 24.0714 29.7151 26.3739 29.7151 29.2142C29.7151 32.0545 32.0177 34.3571 34.858 34.3571H53.1437C55.984 34.3571 58.2865 36.6596 58.2865 39.4999C58.2865 42.3403 55.984 44.6428 53.1437 44.6428H25.7151C22.8748 44.6428 20.5723 46.9453 20.5723 49.7856C20.5723 52.626 22.8748 54.9285 25.7151 54.9285H53.1437C50.3034 54.9285 48.0008 57.231 48.0008 60.0714C48.0008 62.9117 50.3034 65.2142 53.1437 65.2142H117.144C119.984 65.2142 122.287 62.9117 122.287 60.0714C122.287 57.231 119.984 54.9285 117.144 54.9285H115.715C112.875 54.9285 110.572 52.626 110.572 49.7856C110.572 46.9453 112.875 44.6428 115.715 44.6428H122.858C125.698 44.6428 128.001 42.3403 128.001 39.4999C128.001 36.6596 125.698 34.3571 122.858 34.3571H106.858C109.698 34.3571 112.001 32.0545 112.001 29.2142C112.001 26.3739 109.698 24.0714 106.858 24.0714H135.429C138.27 24.0714 140.572 21.7688 140.572 18.9285C140.572 16.0882 138.27 13.7856 135.429 13.7856H64.5723Z"
          fill="#EDE9FE"
        />
        <path
          d="M135.429 44.6428C138.27 44.6428 140.572 42.3403 140.572 39.4999C140.572 36.6596 138.27 34.3571 135.429 34.3571C132.589 34.3571 130.287 36.6596 130.287 39.4999C130.287 42.3403 132.589 44.6428 135.429 44.6428Z"
          fill="#EDE9FE"
        />
        <ellipse cx="80.5723" cy="40.0714" rx="36" ry="36.5714" fill="white" />
        <path
          d="M80.5723 12.25C95.622 12.25 107.822 24.4502 107.822 39.5C107.822 54.5498 95.622 66.75 80.5723 66.75C65.5225 66.75 53.3223 54.5498 53.3223 39.5C53.3223 24.4502 65.5225 12.25 80.5723 12.25Z"
          stroke="#7F22FE"
          strokeWidth="1.5"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M78.9969 35.7784C77.071 35.7784 75.5098 37.3397 75.5098 39.2656C75.5098 41.1915 77.071 42.7528 78.9969 42.7528C80.9229 42.7528 82.4841 41.1915 82.4841 39.2656C82.4841 37.3397 80.9229 35.7784 78.9969 35.7784ZM81.8564 40.6817L81.9397 40.7565L89.1582 47.9764C89.5586 48.3769 89.5586 49.0263 89.158 49.4268C88.7841 49.8006 88.1935 49.8255 87.7908 49.5015L87.7075 49.4267L80.4891 42.2068C80.0886 41.8062 80.0887 41.1568 80.4892 40.7563C80.8631 40.3826 81.4537 40.3577 81.8564 40.6817ZM78.9969 37.8297C79.79 37.8297 80.4328 38.4726 80.4328 39.2656C80.4328 40.0586 79.79 40.7015 78.9969 40.7015C78.2039 40.7015 77.561 40.0586 77.561 39.2656C77.561 38.4726 78.2039 37.8297 78.9969 37.8297ZM78.9969 16.8047C79.528 16.8047 79.9648 17.2083 80.0173 17.7255L80.0226 17.8303V36.8041C80.0226 37.3705 79.5634 37.8297 78.9969 37.8297C78.4659 37.8297 78.0291 37.4261 77.9766 36.9089L77.9713 36.8041V17.8303C77.9713 17.2639 78.4305 16.8047 78.9969 16.8047Z"
          fill="#EDE9FE"
        />
        <path
          d="M80.6373 40.907C81.9968 40.907 83.0989 39.8049 83.0989 38.4454C83.0989 37.086 81.9968 35.9839 80.6373 35.9839C79.2778 35.9839 78.1758 37.086 78.1758 38.4454C78.1758 39.8049 79.2778 40.907 80.6373 40.907Z"
          fill="#EDE9FE"
          stroke="#7F22FE"
          strokeWidth="1.5"
        />
        <path
          d="M80.6387 35.984V17.0103"
          stroke="#7F22FE"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M90.0739 47.881L82.8555 40.6611"
          stroke="#7F22FE"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M81.0469 62.6503V56.9067"
          stroke="#8E51FF"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M57.6621 39.2656H63.4057"
          stroke="#8E51FF"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M97.8691 39.2656H103.613"
          stroke="#8E51FF"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M97.5828 55.8011L93.5215 51.7397"
          stroke="#8E51FF"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M68.575 26.7913L64.5137 22.73"
          stroke="#8E51FF"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M64.5137 55.8011L68.575 51.7397"
          stroke="#8E51FF"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M93.5215 26.7913L97.5828 22.73"
          stroke="#8E51FF"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M115.999 39.4998C115.999 59.0664 100.137 74.9284 80.5706 74.9284C73.9421 74.9284 67.7387 73.108 62.4332 69.9399C62.0888 69.7343 61.6477 69.7839 61.3641 70.0675C60.9746 70.457 61.0452 71.1073 61.5178 71.3903C67.0884 74.7256 73.6056 76.6426 80.5706 76.6426C101.084 76.6426 117.713 60.0132 117.713 39.4998C117.713 18.9864 101.084 2.35693 80.5706 2.35693C60.0572 2.35693 43.4277 18.9864 43.4277 39.4998C43.4277 47.3471 45.8613 54.6259 50.0148 60.6229C50.3151 61.0565 50.9333 61.1033 51.3062 60.7304C51.6047 60.4319 51.6416 59.9614 51.4016 59.6141C47.4541 53.9003 45.142 46.9701 45.142 39.4998C45.142 19.9331 61.0039 4.07122 80.5706 4.07122C100.137 4.07122 115.999 19.9331 115.999 39.4998Z"
          fill="#7F22FE"
        />
        <path
          d="M53.4772 62.3293C53.1485 61.9396 52.5552 61.9058 52.1946 62.2664C51.8856 62.5754 51.8586 63.0684 52.14 63.4028C54.0368 65.6565 56.1966 67.6815 58.5723 69.4305C58.907 69.6769 59.3706 69.6366 59.6645 69.3427C60.0407 68.9665 59.9844 68.3419 59.5563 68.026C57.313 66.3708 55.2721 64.4572 53.4772 62.3293Z"
          fill="#7F22FE"
        />
      </svg>
      <h3 className="mb-2 text-xl font-semibold text-gray-900">Project hours is empty</h3>
      <p className="text-sm text-gray-600">Wait until there&apos;s at least one</p>
    </div>
  );
}
